import React, { useEffect, useRef, useState } from "react";
import {
  enterImmersiveMode,
  exitImmersiveMode,
  isImmersiveActive,
  isStandaloneDisplay,
  isFullscreenActive,
  subscribeFullscreenChange,
} from "../../utils/immersiveMode";
import "./style.css";

/**
 * Full-screen control tuned for Samsung Chrome (S24 etc.).
 * Uses pointerdown/touchstart (not click) so the user-gesture is still valid.
 */
export default function FullscreenCapturePrompt({ targetRef }) {
  const buttonRef = useRef(null);
  const lockRef = useRef(false);
  const [active, setActive] = useState(() =>
    isImmersiveActive(targetRef?.current)
  );
  const [status, setStatus] = useState("");

  const refresh = () => {
    setActive(isImmersiveActive(targetRef?.current));
  };

  useEffect(() => {
    if (isStandaloneDisplay()) {
      setActive(true);
      return undefined;
    }
    refresh();
    return subscribeFullscreenChange(refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const btn = buttonRef.current;
    if (!btn || active || isStandaloneDisplay()) {
      return undefined;
    }

    const run = (event) => {
      // Keep this fully synchronous with the gesture.
      event.preventDefault();
      event.stopPropagation();
      if (lockRef.current) return;
      lockRef.current = true;
      window.setTimeout(() => {
        lockRef.current = false;
      }, 800);

      const target = targetRef?.current || document.documentElement;
      const result = enterImmersiveMode(target);
      Promise.resolve(result).then((info) => {
        refresh();
        if (info && info.native === false) {
          setStatus(
            info.error
              ? `Browser UI may stay visible (${info.error}). Camera is maximized.`
              : "Camera maximized. Browser UI may stay visible on this device."
          );
        } else {
          setStatus("");
        }
      });
    };

    // pointerdown / touchstart preserve user activation on Samsung Chrome.
    btn.addEventListener("pointerdown", run, { passive: false });
    btn.addEventListener("touchstart", run, { passive: false });

    return () => {
      btn.removeEventListener("pointerdown", run);
      btn.removeEventListener("touchstart", run);
    };
  }, [active, targetRef]);

  if (isStandaloneDisplay()) {
    return null;
  }

  if (active) {
    // Native FS hides this; CSS immersive needs an explicit exit.
    if (isFullscreenActive()) {
      return null;
    }
    return (
      <button
        type="button"
        className="pi-fs-prompt pi-fs-prompt--exit"
        onClick={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          await exitImmersiveMode();
          setStatus("");
          refresh();
        }}
      >
        Exit full screen
      </button>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="pi-fs-prompt"
        onClick={(event) => {
          // Desktop / fallback if pointer listeners did not run.
          event.preventDefault();
          event.stopPropagation();
          if (lockRef.current) return;
          lockRef.current = true;
          window.setTimeout(() => {
            lockRef.current = false;
          }, 800);
          Promise.resolve(
            enterImmersiveMode(targetRef?.current || document.documentElement)
          ).then((info) => {
            refresh();
            if (info && info.native === false) {
              setStatus(
                "Camera maximized. Browser UI may stay visible on this device."
              );
            }
          });
        }}
      >
        Tap for full screen
      </button>
      {status ? (
        <div className="pi-fs-prompt pi-fs-prompt--status" role="status">
          {status}
        </div>
      ) : null}
    </>
  );
}
